import { coerceText } from './coerce-text';

export type DateFieldOrder = 'DMY' | 'MDY' | 'auto';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const SEPARATED_DATE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

function padDatePart(value: number | string): string {
  return String(value).padStart(2, '0');
}

function isValidCalendarDate(yyyy: number, mm: number, dd: number): boolean {
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));
  return (
    date.getUTCFullYear() === yyyy &&
    date.getUTCMonth() === mm - 1 &&
    date.getUTCDate() === dd
  );
}

function toIsoDateParts(yyyy: number, mm: number, dd: number): string | undefined {
  if (!isValidCalendarDate(yyyy, mm, dd)) {
    return undefined;
  }
  return `${yyyy}-${padDatePart(mm)}-${padDatePart(dd)}`;
}

function resolveDayMonth(
  first: number,
  second: number,
  order: DateFieldOrder,
): { dd: number; mm: number } | undefined {
  if (order === 'MDY') {
    return { mm: first, dd: second };
  }
  if (order === 'DMY') {
    return { dd: first, mm: second };
  }
  if (first > 12 && second <= 12) {
    return { dd: first, mm: second };
  }
  if (second > 12 && first <= 12) {
    return { mm: first, dd: second };
  }
  return { dd: first, mm: second };
}

/** Converts UI date strings to canonical YYYY-MM-DD for API/DB payloads. */
export function normalizeDateForApi(
  value: unknown,
  order: DateFieldOrder = 'DMY',
): string | undefined {
  const text = coerceText(value).trim();
  if (!text || text === '—') {
    return undefined;
  }

  const iso = ISO_DATE.exec(text);
  if (iso) {
    return toIsoDateParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  const separated = SEPARATED_DATE.exec(text);
  if (separated) {
    const first = Number(separated[1]);
    const second = Number(separated[2]);
    const yyyy = Number(separated[3]);
    const resolved = resolveDayMonth(first, second, order);
    if (!resolved) {
      return undefined;
    }
    return toIsoDateParts(yyyy, resolved.mm, resolved.dd);
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return toIsoDateParts(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth() + 1,
      parsed.getUTCDate(),
    );
  }

  return undefined;
}
